import Button from '@/features/shared/Button';
import Image from '@/features/shared/Image';
import {
  MuiButtonBase,
  MuiCard,
  MuiCardActions,
  MuiCardContent,
  MuiTypography,
} from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import SwiperCarousel from '@/features/shared/SwiperCarousel';
import { getPageUrl, mapLinkToProps } from '@/utils/helpers';
import { BrPageContext } from '@bloomreach/react-sdk';
import { useMediaQuery } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React, { memo, useEffect, useState } from 'react';
import { SwiperSlide } from 'swiper/react';
import useStyles from './ContentCard.styles';

const ContentCardContent = memo(
  ({
    cardCategory,
    cardLogo,
    cardDescription,
    cardHeading,
    buttonLink,
    customerStoryCardInfo,
    cardColor,
    theme,
    cardUrl = '',
    ...restProps
  }: any) => {
    const classes = useStyles({});
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const page = React.useContext(BrPageContext);
    const cardHref = page && getPageUrl({ url: cardUrl || '', page });
    const cardLinkProps = mapLinkToProps({ href: cardHref });
    const hasCardUrl = isMobile && !!cardUrl;

    return (
      <div className={classes.container} {...restProps}>
        {hasCardUrl && <MuiButtonBase {...cardLinkProps} className={classes.cardLink} />}
        <div className={classes.top}>
          {cardCategory && (
            <MuiTypography variant="overline" component="h5" className={classes.contentCategory}>
              {cardCategory.label || cardCategory}
            </MuiTypography>
          )}

          {(cardDescription || cardHeading) && (
            <MuiCardContent className={classes.content}>
              {cardLogo?.original?.url && (
                <div className={clsx(classes.cardLogo, 'card-logo')}>
                  <Image
                    key={cardLogo?.original?.url}
                    image={{ ...cardLogo, fullWidthImage: false }}
                    base="original"
                    position="left"
                    roundedCorners={false}
                  />
                </div>
              )}
              {cardHeading && (
                <MuiTypography
                  variant="h4"
                  component="h4"
                  className={clsx(classes.contentHeading, 'gtm-headline')}
                >
                  {cardHeading}
                </MuiTypography>
              )}
              {cardDescription && (
                <CaptureClicksRichText
                  theme={theme}
                  className={clsx('card-description', classes.copy)}
                  content={cardDescription}
                />
              )}
            </MuiCardContent>
          )}
        </div>
        {(buttonLink || customerStoryCardInfo) && (
          <div className={classes.bottom}>
            <MuiCardActions className={clsx('card-actions', classes.actions)}>
              {customerStoryCardInfo && (
                <CaptureClicksRichText
                  theme={theme}
                  className={classes.customerNameTitle}
                  content={customerStoryCardInfo}
                />
              )}
              {buttonLink && (
                <Button
                  theme={theme}
                  className={clsx(classes.currentCardCta, 'gtm-cta')}
                  link={buttonLink}
                  data-datalayer-content-card-link="true"
                />
              )}
            </MuiCardActions>
          </div>
        )}
      </div>
    );
  },
);

const ContentCard = ({
  data = [],
  cardColor,
  theme,
  className: overrideClassName,
  elevation = 8,
  index,
}: {
  data?: CardType[];
  cardColor?: string;
  theme?: ThemeType;
  className?: string;
  elevation?: number;
  index?: number;
}): JSX.Element => {
  const [slider, setSlider] = useState<any>(null);
  const classes = useStyles({});
  const rootClassName = clsx(
    classes.root,
    `${theme}-theme`,
    `bg-color-${cardColor}`,
    overrideClassName,
    'card-root',
  );

  const sliderProps = {
    spaceBetween: 0,
    slidesPerView: 1,
    loop: true,
    loopedSlides: data.length,
    roundLengths: true,
    allowTouchMove: false,
    onSwiper: (swiper: any) => {
      setSlider(swiper);
      swiper.slideTo(index, 0, false);
    },
  };

  useEffect(() => {
    if (slider && !slider.destroyed && data.length > 1) {
      slider.slideTo(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  return (
    <MuiCard className={clsx(rootClassName, 'gntc-content-card')} elevation={elevation}>
      {data.length > 1 ? (
        <SwiperCarousel
          slider={slider}
          className={classes.carousel}
          {...sliderProps}
          classes={classes}
        >
          {data.map((cData, idx) => {
            const {
              cardCategory,
              cardLogo,
              cardDescription,
              cardHeading,
              buttonLink,
              customerStoryCardInfo,
              url,
            } = cData || {};

            return (
              // eslint-disable-next-line react/no-array-index-key
              <SwiperSlide key={`card_${idx}`}>
                <ContentCardContent
                  cardCategory={cardCategory}
                  cardLogo={cardLogo}
                  cardDescription={cardDescription}
                  cardHeading={cardHeading}
                  buttonLink={buttonLink}
                  customerStoryCardInfo={customerStoryCardInfo}
                  theme={theme}
                  cardColor={cardColor}
                  cardUrl={url}
                  data-datalayer-card-index={idx}
                  data-datalayer-card-title={cardHeading}
                />
              </SwiperSlide>
            );
          })}
        </SwiperCarousel>
      ) : (
        data[0] && (
          <ContentCardContent
            cardCategory={data[0].cardCategory}
            cardLogo={data[0].cardLogo}
            cardDescription={data[0].cardDescription}
            cardHeading={data[0].cardHeading}
            buttonLink={data[0].buttonLink}
            customerStoryCardInfo={data[0].customerStoryCardInfo}
            theme={theme}
            cardColor={cardColor}
            cardUrl={data[0].url}
          />
        )
      )}
    </MuiCard>
  );
};

export default memo(ContentCard);
